import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import axios from 'axios';
import {
  GraduationCap, Users, Award, CheckCircle, ArrowRight,
  Building2, BookOpen, Shield, Heart, Star, Quote, Play, Home, Monitor, FileText, Briefcase,
} from 'lucide-react';

import { API } from '@/config';
import { TrustBanner } from '@/components/TrustBanner';

const ICON_MAP = {
  BookOpen, Award, Users, Shield, Heart, GraduationCap, Building2, CheckCircle, Star,
};

const HomePage = () => {
  const [content, setContent] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [residentCourses, setResidentCourses] = useState([]);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    axios.get(`${API}/cms/homepage`).then(res => setContent(res.data)).catch(() => {}).finally(() => setLoaded(true));
    axios.get(`${API}/courses/public?course_type=resident&limit=4`).then(res => setResidentCourses(res.data)).catch(() => {});
  }, []);

  // Defaults if API hasn't loaded yet
  const hero = content?.hero || {
    headline: 'Supporting Those Who Care for Others',
    headline_accent: 'Care for Others',
    subheadline: 'Thoughtful, accessible training for care workers, support staff, and organisations.',
    cta_text: 'Explore Training',
    cta_link: '/courses',
    secondary_cta_text: 'For Organisations',
    secondary_cta_link: '/register?type=org',
    badge_text: 'Care Sector Training',
  };
  const stats = content?.stats || [
    { value: '500+', label: 'Organisations' },
    { value: '10,000+', label: 'Care Workers Trained' },
    { value: '50+', label: 'Accredited Courses' },
    { value: '98%', label: 'Completion Rate' },
  ];
  const featuresHeading = content?.features_heading || 'Why Choose Common Impact?';
  const featuresSub = content?.features_subheading || 'Our platform is built specifically for the care sector.';
  const features = content?.features || [
    { title: 'Thoughtful Courses', description: 'Training designed for care workers.', icon: 'BookOpen' },
    { title: 'Verified Certificates', description: 'Earn certificates with QR verification.', icon: 'Award' },
    { title: 'Team Management', description: 'Manage team training progress.', icon: 'Users' },
    { title: 'Compliance Ready', description: 'Training that meets CQC requirements.', icon: 'Shield' },
  ];
  const testimonials = content?.testimonials || [];
  const ctaHeading = content?.cta_heading || 'Ready to Start Your Training?';
  const ctaSub = content?.cta_subheading || 'Join thousands of care professionals building their skills.';

  const featureColors = ['text-[#20acf6]', 'text-[#02d1d4]', 'text-[#2a3e5d]', 'text-[#02d1d4]'];

  // Build hero headline with accent
  const renderHeadline = () => {
    const full = hero.headline || '';
    const accent = hero.headline_accent || '';
    if (!accent || !full.includes(accent)) {
      return <span>{full}</span>;
    }
    const idx = full.indexOf(accent);
    return (
      <>
        {full.substring(0, idx)}
        <span className="text-[#20acf6]">{accent}</span>
        {full.substring(idx + accent.length)}
      </>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]" data-testid="home-page">
      <Helmet>
        <title>Common Impact E-Learning | Care Sector Training Platform</title>
        <meta name="description" content="Common Impact delivers specialist e-learning for the care and housing provider sector. CQC-ready compliance training, verifiable certificates, and team management for social landlords, care homes, and councils." />
        <meta property="og:title" content="Common Impact E-Learning | Care Sector Training Platform" />
        <meta property="og:description" content="Specialist e-learning for care and housing professionals. Interactive courses, verifiable certificates, compliance dashboards." />
        <meta property="og:type" content="website" />
        <script type="text/javascript" src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js" async></script>
      </Helmet>
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#f8fafc] via-[#f8fafc]/60 to-[#F8FAFC]" />
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#20acf6]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#02d1d4]/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#20acf6]/15 text-[#2a3e5d] mb-6">
                <Heart className="w-3 h-3 mr-1.5" />
                {hero.badge_text}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#2a3e5d] mb-6 leading-tight">
                {renderHeadline()}
              </h1>
              <p className="text-lg text-[#1e293b]/70 mb-8 leading-relaxed max-w-xl">
                {hero.subheadline}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-[#02d1d4] hover:bg-[#02bfc2] text-white rounded-xl px-6 shadow-lg shadow-[#02d1d4]/25 ring-1 ring-[#02d1d4]/30" data-testid="hero-try-free">
                  <Link to={isAuthenticated ? '/courses?price=free' : '/register?redirect=/courses?price=free'}>
                    <Play className="h-4 w-4 mr-2" /> Try Free Courses
                  </Link>
                </Button>
                <Button asChild size="lg" className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white rounded-xl px-6 shadow-md shadow-[#20acf6]/20">
                  <Link to={hero.cta_link} data-testid="hero-browse-courses">
                    View All Courses
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-[#02d1d4] text-[#1e293b] hover:bg-[#f8fafc] rounded-xl px-6">
                  <Link to={hero.secondary_cta_link} data-testid="hero-org-signup">
                    {hero.secondary_cta_text}
                  </Link>
                </Button>
              </div>
            </div>

            <div className="hidden lg:block">
              <img
                src="https://customer-assets.emergentagent.com/job_accessible-courses-1/artifacts/855esmkn_Support%20Services%20INTU%20Housing%20Solutions%202.jpg"
                alt="Support worker helping elderly person with a tablet"
                className="rounded-2xl shadow-lg border border-[#f8fafc]"
              />
            </div>
          </div>
        </div>
      </section>

      <TrustBanner />

      {/* Stats Section */}
      {stats.length > 0 && (
        <section className="bg-white py-12 border-y border-[#f8fafc]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-[#20acf6] mb-1">{stat.value}</div>
                  <div className="text-sm text-[#1e293b]/60">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-20 lg:py-28 bg-[#F8FAFC]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#2a3e5d] mb-4">{featuresHeading}</h2>
            <p className="text-base lg:text-lg text-[#1e293b]/60 max-w-2xl mx-auto leading-relaxed">
              This platform provides trusted, high-quality training for the entire care sector. Whether you're looking to upskill your team, build independence, or start your career in care, we have a course for you.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = ICON_MAP[feature.icon] || BookOpen;
              return (
                <Card key={index} className="border-[#f8fafc] hover:shadow-md transition-all duration-200 bg-white">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#f8fafc] rounded-xl flex items-center justify-center mb-4">
                      <Icon className={`w-6 h-6 ${featureColors[index % featureColors.length]}`} />
                    </div>
                    <h3 className="text-lg font-semibold text-[#1e293b] mb-2">{feature.title}</h3>
                    <p className="text-sm text-[#1e293b]/60 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image Section — between Features and Training for Everyone */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200"
            alt="Team collaborating on training"
            className="w-full rounded-2xl shadow-lg border border-[#f8fafc]"
          />
        </div>
      </section>

      {/* Training for Everyone Section */}
      <section className="py-20 lg:py-28 bg-white" data-testid="training-for-everyone-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#20acf6]/10 text-[#20acf6] mb-4">
              <Users className="w-3 h-3 mr-1.5" /> Three Pathways
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#2a3e5d] mb-4">
              Training for Everyone — Find Your Path
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Card 1: For Organisations */}
            <Card className="border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col" data-testid="tfe-card-organisations">
              <div className="h-2 bg-[#02d1d4]" />
              <CardContent className="p-6 flex flex-col flex-1">
                <div className="w-12 h-12 bg-[#02d1d4]/10 rounded-xl flex items-center justify-center mb-4">
                  <Building2 className="h-6 w-6 text-[#02d1d4]" />
                </div>
                <h3 className="text-lg font-bold text-[#2a3e5d] mb-3">For Organisations</h3>
                <p className="text-sm text-[#1e293b]/70 leading-relaxed mb-5">
                  Care homes, RSLs, and managing agents trust Common Impact to deliver consistent, quality training.
                  Our platform gives you centralised team management, real-time progress tracking, bulk enrolment,
                  and compliance-ready certificate verification — all designed specifically for the care sector.
                </p>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {['Centralised team management dashboard', 'Real-time progress tracking and reporting', 'Bulk enrolment and team seat management', 'Compliance-ready certificate verification'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#1e293b]/80">
                      <CheckCircle className="w-4 h-4 text-[#02d1d4] mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button asChild className="w-full bg-[#02d1d4] hover:bg-[#00acd0] text-white rounded-xl" data-testid="tfe-org-btn">
                  <Link to="/register?type=org"><Building2 className="h-4 w-4 mr-2" /> Register Your Organisation</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Card 2: For Residents */}
            <Card className="border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col" data-testid="tfe-card-residents">
              <div className="h-2 bg-[#10B981]" />
              <CardContent className="p-6 flex flex-col flex-1">
                <div className="w-12 h-12 bg-[#10B981]/10 rounded-xl flex items-center justify-center mb-4">
                  <Home className="h-6 w-6 text-[#10B981]" />
                </div>
                <h3 className="text-lg font-bold text-[#2a3e5d] mb-3">For Residents</h3>
                <p className="text-sm text-[#1e293b]/70 leading-relaxed mb-5">
                  Residents in supported living and care settings can build independence with free courses
                  on digital skills, CV writing, interview preparation, and workplace essentials.
                  Start your journey to independence today.
                </p>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {['Free, self-paced courses for residents', 'Digital skills and online safety', 'CV writing and interview preparation', 'Introduction to the workplace'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#1e293b]/80">
                      <CheckCircle className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button asChild className="w-full bg-[#10B981] hover:bg-[#059669] text-white rounded-xl" data-testid="tfe-resident-btn">
                  <Link to="/register?type=resident"><Home className="h-4 w-4 mr-2" /> Register as a Resident</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Card 3: For Individual Learners */}
            <Card className="border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col" data-testid="tfe-card-learners">
              <div className="h-2 bg-[#20acf6]" />
              <CardContent className="p-6 flex flex-col flex-1">
                <div className="w-12 h-12 bg-[#20acf6]/10 rounded-xl flex items-center justify-center mb-4">
                  <GraduationCap className="h-6 w-6 text-[#20acf6]" />
                </div>
                <h3 className="text-lg font-bold text-[#2a3e5d] mb-3">For Individual Learners</h3>
                <p className="text-sm text-[#1e293b]/70 leading-relaxed mb-5">
                  Start or advance your career in care with flexible, accessible courses designed to help
                  you gain the skills and qualifications you need to succeed.
                </p>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {['CQC-aligned training', 'CPD-accredited courses', 'Flexible, self-paced learning', 'Certificates upon completion'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#1e293b]/80">
                      <CheckCircle className="w-4 h-4 text-[#20acf6] mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button asChild className="w-full bg-[#20acf6] hover:bg-[#3bb8f7] text-white rounded-xl" data-testid="tfe-learner-btn">
                  <Link to="/register"><GraduationCap className="h-4 w-4 mr-2" /> Start Your Career in Care</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#20acf6] to-[#3bb8f7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <GraduationCap className="w-14 h-14 mx-auto mb-6 text-white/80" />
          <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-white">{ctaHeading}</h2>
          <p className="text-base lg:text-lg text-white/80 mb-8 max-w-2xl mx-auto">{ctaSub}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-white text-[#20acf6] hover:bg-[#f8fafc] rounded-xl px-6 shadow-lg">
              <Link to="/register" data-testid="cta-signup">Create Free Account</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 rounded-xl px-6">
              <Link to="/courses">Explore Courses</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Reviews & Testimonials */}
      <section className="py-16 lg:py-20 bg-[#F8FAFC]" data-testid="reviews-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#2a3e5d] mb-3">What Our Learners Say</h2>
            <p className="text-base text-slate-500 max-w-xl mx-auto">
              Real feedback from care professionals and organisations using Common Impact.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
            {[
              { quote: "The Safeguarding course was clear, practical, and gave me the confidence to handle real situations at work. I earned my certificate in just a few hours \u2014 perfect for busy care workers like me.", name: "Sarah T.", role: "Support Worker", type: "Learner" },
              { quote: "We\u2019ve struggled to track training compliance across our supported housing schemes. Common Impact gives us a clear view of who\u2019s completed what, and the CQC-ready reports are a game-changer.", name: "James R.", role: "Managing Agent", type: "Managing Agent" },
              { quote: "We needed consistent, high-quality training for staff across multiple sites. Common Impact delivered with engaging content and real-time progress tracking. It\u2019s now our go-to training provider.", name: "Linda M.", role: "RSL Training Lead", type: "Registered Social Landlord" },
              { quote: "Since using Common Impact, our staff feel more confident and prepared. The courses are practical, the certificates are recognised, and the platform is so easy to use. Highly recommended.", name: "David K.", role: "Care Home Manager", type: "Care Company" },
            ].map((t, i) => (
              <div key={i} className="relative bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`testimonial-${i}`}>
                <Quote className="h-6 w-6 text-[#20acf6]/20 absolute top-4 right-4" />
                <div className="flex mb-2.5">
                  {[...Array(5)].map((_, si) => <Star key={si} className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />)}
                </div>
                <p className="text-sm text-slate-600 italic leading-relaxed mb-4">"{t.quote}"</p>
                <div className="border-t border-slate-100 pt-3">
                  <p className="text-sm font-semibold text-[#2a3e5d]">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Trustpilot Widget */}
          <div className="max-w-2xl mx-auto mb-8" data-testid="trustpilot-widget-container">
            <div
              className="trustpilot-widget"
              data-locale="en-US"
              data-template-id="56278e9abfbbba0bdcd568bc"
              data-businessunit-id="6a67878636c62c128ad0426e"
              data-style-height="52px"
              data-style-width="100%"
              data-token="1046e998-f5ed-4a1d-bf6c-3fece9bd4298"
            >
              <a href="https://www.trustpilot.com/review/commonimpactelearning.co.uk" target="_blank" rel="noopener noreferrer">Trustpilot</a>
            </div>
          </div>

          {/* Leave a Review CTA */}
          <div className="text-center" data-testid="review-cta">
            <p className="text-sm font-medium text-[#2a3e5d] mb-4">Enjoyed our training? Leave us a review!</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button asChild className="bg-[#00b67a] hover:bg-[#00a06a] text-white rounded-xl px-5" data-testid="trustpilot-review-btn">
                <a href="https://www.trustpilot.com/review/commonimpactelearning.co.uk" target="_blank" rel="noopener noreferrer">
                  <Star className="h-4 w-4 mr-2 fill-white" /> Review on Trustpilot
                </a>
              </Button>
              <Button asChild variant="outline" className="border-[#2a3e5d] text-[#2a3e5d] hover:bg-[#2a3e5d]/5 rounded-xl px-5" data-testid="google-review-btn">
                <a href="https://g.page/r/commonimpactelearning/review" target="_blank" rel="noopener noreferrer">
                  <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Review on Google
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
